<script>
  import { onMount, onDestroy, createEventDispatcher } from 'svelte';
  import { getContext } from 'svelte';
  import { canvasStore } from '../stores/canvasStore.js';
  import { get } from 'svelte/store';

  const selectedSlideIndex = getContext('selectedSlideIndex');
  const storyStore = getContext('storyStore');

  let fabric;
  let canvas;
  // Remove global canvasStates; use per-slide state from storyStore only
  let currentStateIndex = -1;
  let selectedObject = null;
  let isImageLoading = false;
  let clipboard = null; // For copy/paste operations
  let canvasElement; // Reference to canvas DOM element
  let canvasContainer; // Reference to canvas container

  const dispatch = createEventDispatcher();

  // Initialize Fabric.js canvas on component mount
  onMount(() => {
    fabric = window.fabric;
    if (!fabric) {
      console.error('Fabric.js not loaded');
      return;
    }

    canvas = new fabric.Canvas('story-canvas', {
      height: 667,
      width: 375,
      backgroundColor: 'white',
    });

    canvasElement = document.getElementById('story-canvas');
    canvasContainer = document.querySelector('.canvas-container2');
    
    console.log('Canvas initialized:', canvas);

    dispatch('canvasInstance', canvas);
    canvasStore.update(store => ({ ...store, canvas }));

    // Load the initial slide content
    loadSlideContent($selectedSlideIndex);

    // Event listeners for canvas changes
    setupCanvasEventListeners();

    // Keyboard event listener 
  window.addEventListener('keydown', handleCanvasKeyDown);
  });

  // Cleanup on component destroy
  onDestroy(() => {
    if (canvas) {
      canvas.dispose();
      console.log('Canvas disposed');
    }
    //remove keyboard event listener
    window.removeEventListener('keydown', handleCanvasKeyDown);
  });

  // Setup canvas event listeners
  function setupCanvasEventListeners() {
    canvas.on('object:added', saveCanvasState);
    canvas.on('object:modified', saveCanvasState);
    canvas.on('object:removed', saveCanvasState);
    canvas.on('selection:created', handleObjectSelected);
    canvas.on('selection:cleared', handleSelectionCleared);
    canvas.on('selection:updated', handleObjectSelected);
  }

  // Text management system
  const textManager = {
    // Default text options
    defaultOptions: {
      width: 100,
      left: 100,
      top: 100,
      fontSize: 24,
      fontFamily: 'Arial',
      fill: '#000',
      editable: true,
      selectable: true,
      hasControls: true,
      objectCaching: false,
      transparentCorners: false,
      padding: 10,
      textAlign: 'left',
      lineHeight: 1.2,
      charSpacing: 0,
      minWidth: 20,
      splitByGrapheme: false,
      styles: [],
      direction: 'ltr',
      lockScalingY: true,
      lockScalingFlip: true,
      lockMovementX: false,
      lockMovementY: false
    },

    // Initialize a text object with proper event handlers
    initializeTextObject(textObject) {
      // Handle text changes
      textObject.on('changed', () => {
        saveCanvasState();
      });

      return textObject;
    },

    // Create a new text object with the given text and options
    createTextObject(text, options = {}) {
      const mergedOptions = {
        ...this.defaultOptions,
        ...options
      };

      const textObject = new fabric.Textbox(text, mergedOptions);
      return this.initializeTextObject(textObject);
    },

    // Add text to canvas with proper positioning and initialization
    addTextToCanvas(text, options = {}) {
      console.log(`Adding text to canvas: ${text}`, options);

      const textObject = this.createTextObject(text, options);
      
      // Center the text if no specific position is provided
      if (!options.left && !options.top) {
        const canvasWidth = canvas.width;
        const canvasHeight = canvas.height;
        textObject.set({
          left: (canvasWidth - textObject.width) / 2,
          top: (canvasHeight - textObject.height) / 2
        });
      }

      canvas.add(textObject);
      canvas.setActiveObject(textObject);
      saveCanvasState();

      return textObject;
    },

    // Update text properties
    updateTextProperties(textObject, properties) {
      if (!textObject || !textObject.type.includes('text')) return;
      
      textObject.set(properties);
      canvas.renderAll();
      saveCanvasState();
    },

    // Set text color
    setTextColor(textObject, color) {
      this.updateTextProperties(textObject, { fill: color });
    },

    // Set font size
    setFontSize(textObject, size) {
      this.updateTextProperties(textObject, { fontSize: parseInt(size) });
    },

    // Set font family
    setFontFamily(textObject, fontFamily) {
      this.updateTextProperties(textObject, { fontFamily });
    },

    // Set text alignment
    setTextAlign(textObject, alignment) {
      this.updateTextProperties(textObject, { textAlign: alignment });
    },

    // Toggle bold
    toggleBold(textObject) {
      if (!textObject || !textObject.type.includes('text')) return;
      
      textObject.set('fontWeight', textObject.fontWeight === 'bold' ? 'normal' : 'bold');
      canvas.renderAll();
      saveCanvasState();
    },

    // Toggle italic
    toggleItalic(textObject) {
      if (!textObject || !textObject.type.includes('text')) return;
      
      textObject.set('fontStyle', textObject.fontStyle === 'italic' ? 'normal' : 'italic');
      canvas.renderAll();
      saveCanvasState();
    },

    // Toggle underline
    toggleUnderline(textObject) {
      if (!textObject || !textObject.type.includes('text')) return;
      
      textObject.set('underline', !textObject.underline);
      canvas.renderAll();
      saveCanvasState();
    }
  };

  // Export the text management functions
  export function addTextToCanvas(text, options = {}) {
    return textManager.addTextToCanvas(text, options);
  }

  export function setTextColor(color) {
    if (selectedObject) {
      textManager.setTextColor(selectedObject, color);
    }
  }

  export function setFontSize(size) {
    if (selectedObject) {
      textManager.setFontSize(selectedObject, size);
    }
  }

  export function setFontFamily(fontFamily) {
    if (selectedObject) {
      textManager.setFontFamily(selectedObject, fontFamily);
    }
  }

  export function setTextAlign(alignment) {
    if (selectedObject) {
      textManager.setTextAlign(selectedObject, alignment);
    }
  }

  export function toggleBold() {
    if (selectedObject) {
      textManager.toggleBold(selectedObject);
    }
  }

  export function toggleItalic() {
    if (selectedObject) {
      textManager.toggleItalic(selectedObject);
    }
  }

  export function toggleUnderline() {
    if (selectedObject) {
      textManager.toggleUnderline(selectedObject);
    }
  }

  // Handle object selection
  function handleObjectSelected(event) {
    if (event.selected && event.selected.length > 0) {
      const obj = event.selected[0];
      selectedObject = obj;
      
      // Determine the type of object
      const objectType = getObjectType(obj);
      
      // Get evidence data from metadata if it exists
      const metadata = obj.metadata || {};
      const evidenceData = metadata.isEvidence ? metadata.evidenceData : null;
      
      console.log('Object selected:', {
        type: objectType,
        objectType: obj.type,
        isEvidence: metadata.isEvidence,
        evidenceData: evidenceData,
        object: obj,
        properties: {
          text: obj.text,
          fontFamily: obj.fontFamily,
          fontSize: obj.fontSize,
          fontWeight: obj.fontWeight,
          fontStyle: obj.fontStyle,
          underline: obj.underline,
          textAlign: obj.textAlign,
          fill: obj.fill
        }
      });
      
      // Ensure the object is the active object
      canvas.setActiveObject(obj);
      
      dispatch('updateSubMenu', { 
        selectedObject: obj, 
        objectType, 
        evidenceData,
        metadata 
      });
    } else {
      handleSelectionCleared();
    }
  }

  // Handle selection cleared
  function handleSelectionCleared() {
    selectedObject = null;
    canvas.discardActiveObject();
    console.log('Selection cleared');
    dispatch('updateSubMenu', { 
      selectedObject: null, 
      objectType: null, 
      evidenceData: null,
      metadata: null 
    });
  }

  // Keyboard event handlers
  function handleCanvasKeyDown(event) {
    // Don't handle shortcuts if user is typing in a text object
    if (selectedObject && selectedObject.isEditing) {
      return;
    }

    // Don't handle shortcuts if user is typing in an input/textarea
    if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
      return;
    }

    // Check for modifier keys
    const isCtrlOrCmd = event.ctrlKey || event.metaKey;
    const isShift = event.shiftKey;
    const isAlt = event.altKey;

    // Handle different keyboard shortcuts
    switch (event.key.toLowerCase()) {
      case 'delete':
      case 'backspace':
        if (selectedObject) {
          deleteSelectedObject();
        }
        break;

      case 'c':
        if (isCtrlOrCmd) {
          event.preventDefault();
          copySelectedObject();
        }
        break;

      case 'v':
        if (isCtrlOrCmd) {
          event.preventDefault();
          if (clipboard) {
            pasteObject();
          } else {
            triggerPaste();
          }
        }
        break;

      case 'x':
        if (isCtrlOrCmd) {
          event.preventDefault();
          cutSelectedObject();
        }
        break;

      case 'z':
        if (isCtrlOrCmd) {
          event.preventDefault();
          if (isShift) {
            console.log('Redo triggered'); //testing
            redoCanvasState();
          } else {
            undoCanvasState();
          }
        }
        break;

      case 'y':
        if (isCtrlOrCmd) {
          event.preventDefault();
          redoCanvasState();
        }
        break;

      case 'a':
        if (isCtrlOrCmd) {
          event.preventDefault();
          selectAllObjects();
        }
        break;

      case 'escape':
        if (selectedObject && selectedObject.isEditing) {
          selectedObject.exitEditing();
        } else {
          canvas.discardActiveObject();
        }
        break;

      // Arrow keys for moving selected object
      case 'arrowup':
        if (selectedObject && !selectedObject.isEditing) {
          event.preventDefault();
          moveSelectedObject(0, -10);
        }
        break;

      case 'arrowdown':
        if (selectedObject && !selectedObject.isEditing) {
          event.preventDefault();
          moveSelectedObject(0, 10);
        }
        break;

      case 'arrowleft':
        if (selectedObject && !selectedObject.isEditing) {
          event.preventDefault();
          moveSelectedObject(-10, 0);
        }
        break;

      case 'arrowright':
        if (selectedObject && !selectedObject.isEditing) {
          event.preventDefault();
          moveSelectedObject(10, 0);
        }
        break;
    }
  }
//copy selected object and paste in clipboard
  function copySelectedObject() {
    if (selectedObject) {
      selectedObject.clone(cloned => {
        clipboard = cloned;
        console.log("Copied to clipboard:", clipboard);
    });
  }
}
//paste copy of object from clipboard onto canvas
  function pasteObject() {
    if (clipboard) {
      clipboard.clone(clonedObj => {
        canvas.discardActiveObject(); //clear current selection before adding new object
        clonedObj.set({
          left: clonedObj.left + 10,
          top: clonedObj.top + 10,
          evented: true,
    });

        canvas.add(clonedObj); //adds object to canvas
        canvas.setActiveObject(clonedObj); //select copied object
        canvas.renderAll();
        saveCanvasState();
    });
  }
}


  function getObjectType(obj) {
    if (obj.type === 'text' || obj.type === 'i-text' || obj.type === 'textbox') return 'text';
    if (obj.type === 'image') return 'image';
    if (obj.type === 'circle') return 'circle';
    if (obj.type === 'rect') return 'rect';
    if (obj.type === 'triangle') return 'triangle';
    if (obj.type === 'line') return 'line';
    if (obj.type === 'path') return 'drawing';
    return 'other';
  }

  // Reactive statement to watch for slide index changes
  $: {
    if ($selectedSlideIndex !== undefined) {
      loadSlideContent($selectedSlideIndex);
    }
  }

  /**
   * Load content for the selected slide
   * @param {number} index - The index of the selected slide
   */
  function loadSlideContent(slideIndex) {
    if (!canvas) {
      return;
    }
    window.isSlideSwitching = true;
    setTimeout(() => { window.isSlideSwitching = false; }, 300);
    if (slideIndex === undefined || slideIndex === null) {
      return;
    }
    const slides = $storyStore?.slides || [];
    if (!slides || slides.length === 0) {
      return;
    }
    if (slideIndex >= slides.length) {
      return;
    }
    const currentSlide = slides[slideIndex];
    if (!currentSlide) {
      return;
    }
    canvas.clear();
    // Load canvas state if it exists
    if (currentSlide.canvasState && currentSlide.canvasState.length > 0) {
      try {
        // Get the full (uncompressed) canvas state
        const fullState = storyStore.getFullCanvasState(slideIndex);
        // Deep clone before loading
        const clonedState = JSON.parse(JSON.stringify(fullState));
        canvas.loadFromJSON({ objects: clonedState }, () => {
          canvas.renderAll();
        });
      } catch (error) {
        // fail silently
      }
    }
  }

  let isUndoRedoInProgress = false; //flag
  /**
   * Save the current canvas state to the store
   */
  function saveCanvasState() {
    // Don't save if in the middle of undo/redo or rapid slide switching
    if (isUndoRedoInProgress || window.isSlideSwitching) {
      return;
    }
    const index = get(selectedSlideIndex);
    if (index === undefined || index < 0) return;
    // Save only the current slide's state, deep cloned
    const propertiesToSave = [
      'selectable','hasControls','text','fontFamily','fontSize','fontWeight','fontStyle','lineHeight','underline','overline','linethrough','textAlign','fill','stroke','strokeWidth','left','top','width','height','scaleX','scaleY','angle','opacity','src','crossOrigin','metadata'
    ];
    // Ensure clipboard images are saved as data URLs
    canvas.getObjects().forEach(obj => {
      if (obj.type === 'image' && obj.metadata && obj.metadata.isClipboardImage && obj.metadata.embeddedDataUrl) {
        obj.setSrc(obj.metadata.embeddedDataUrl, () => {}, { crossOrigin: 'anonymous' });
      }
    });
    const canvasJSON = canvas.toJSON(propertiesToSave);
    // Deep clone before saving
    const clonedObjects = JSON.parse(JSON.stringify(canvasJSON.objects));
    storyStore.updateCanvasState(index, clonedObjects);
  }

  export function undoCanvasState() {
    console.log('Undo triggered');
    console.log('Before undo → currentStateIndex:', currentStateIndex, 'canvasStates length:', canvasStates.length);

    if (currentStateIndex > 0) {
      isUndoRedoInProgress = true;  // Setup flag before undo

      currentStateIndex--; // Go back
      const previousState = canvasStates[currentStateIndex];

      canvas.loadFromJSON(previousState, () => {
        canvas.renderAll(); 
        console.log('Canvas state reverted to previous state');
        console.log('After undo → currentStateIndex:', currentStateIndex);

        isUndoRedoInProgress = false; // Clear flag after undo

      });
    } else {
      console.warn('No more states to undo');
    }
  }

  /*
  * Redo the last undone action on the canvas.
  * Moves forward in the canvas state history and restores the next saved state.
  * Triggered by Ctrl+Shift+Z/Redo button.
  */

  export function redoCanvasState() {
    console.log('Redo triggered');
    console.log('Before redo → currentStateIndex:', currentStateIndex, 'canvasStates length:', canvasStates.length);

    // Check if we're not already at the latest state
    if (currentStateIndex < canvasStates.length - 1) {
      isUndoRedoInProgress = true; // Set flag BEFORE redo operation

      currentStateIndex++; // Move forward in history timeline

      const nextState = canvasStates[currentStateIndex]; // Get the next state

    // Load the next state onto canvas
      canvas.loadFromJSON(nextState, () => {
        canvas.renderAll(); // show restored state
        console.log('Canvas state redone to next state');
        console.log('After redo → currentStateIndex:', currentStateIndex);

        isUndoRedoInProgress = false; // Clear flag after redo
      });
  } else {
    // No more states to redo
    console.warn('No more states to redo');
  }
}

  export function saveCanvasPreview() {
    return new Promise((resolve, reject) => {
      const index = get(selectedSlideIndex);
      if (index !== undefined && index >= 0) {
        try {
          // First, get the current canvas dimensions
          const originalWidth = canvas.width;
          const originalHeight = canvas.height;
          const scaleFactor = 0.5; // Generate a smaller preview for better performance
          
          // Create a temporary canvas for the preview
          const tempCanvas = new fabric.Canvas(null, {
            width: originalWidth * scaleFactor,
            height: originalHeight * scaleFactor
          });
          
          // Clone all objects from the main canvas
          canvas.getObjects().forEach(obj => {
            const clonedObj = fabric.util.object.clone(obj);
            // Scale the object for the preview
            clonedObj.scaleX = obj.scaleX * scaleFactor;
            clonedObj.scaleY = obj.scaleY * scaleFactor;
            clonedObj.left = obj.left * scaleFactor;
            clonedObj.top = obj.top * scaleFactor;
            tempCanvas.add(clonedObj);
          });
          
          // Generate optimized SVG
          const svgData = tempCanvas.toSVG({
            viewBox: {
              x: 0,
              y: 0,
              width: originalWidth * scaleFactor,
              height: originalHeight * scaleFactor
            },
            width: originalWidth * scaleFactor,
            height: originalHeight * scaleFactor,
            suppressPreamble: false,
            preserveAspectRatio: 'xMidYMid meet'
          });
          
          // Clean up temporary canvas
          tempCanvas.dispose();
          
          // Optimize SVG by removing unnecessary attributes and whitespace
          const optimizedSvg = svgData
            .replace(/\s+/g, ' ') // Reduce whitespace
            .replace(/>\s+</g, '><') // Remove whitespace between tags
            .replace(/\s+>/g, '>') // Remove trailing whitespace in tags
            .replace(/\s+\/>/g, '/>') // Clean self-closing tags
            .replace(/<svg/, `<svg width="${originalWidth * scaleFactor}" height="${originalHeight * scaleFactor}"`);
          
          // Save the optimized preview
          storyStore.updateSlidePreview(index, optimizedSvg);
          
          console.log('Preview saved for slide:', index);
          resolve();
        } catch (err) {
          console.error('Error saving preview:', err);
          reject(err);
        }
      } else {
        const error = new Error('Invalid slide index, cannot save SVG preview');
        console.warn(error.message);
        reject(error);
      }
    });
  }

  /**
   * Add an image to the canvas from evidence object
   * @param {object} evidence - The evidence object containing image URLs
   */
  export function addImageToCanvas(imageData) {
    console.log('Adding image to canvas:', imageData);
    isImageLoading = true;
    
    // Determine if this is an evidence image by checking for required properties
    const isEvidenceImage = imageData.evidence && imageData.evidence.s3URL;
    const imageUrl = isEvidenceImage ? imageData.evidence.s3URL : imageData.url;
    
    return new Promise((resolve, reject) => {
      fabric.Image.fromURL(
        imageUrl, 
        function(img) {
          if (img) {
            // Get canvas dimensions
            const canvasWidth = canvas.width;
            const canvasHeight = canvas.height;
            const maxHeight = canvasHeight * 0.7; // 70% of canvas height
            
            // Calculate aspect ratios
            const imageRatio = img.width / img.height;
            const maxWidthFromHeight = maxHeight * imageRatio;
            
            let finalWidth, finalHeight;
            
            // If width when constrained by height is less than canvas width
            if (maxWidthFromHeight <= canvasWidth) {
              // Use max height as constraint
              finalHeight = maxHeight;
              finalWidth = maxWidthFromHeight;
            } else {
              // Use canvas width as constraint
              finalWidth = canvasWidth;
              finalHeight = canvasWidth / imageRatio;
            }
            
            // Calculate scale factors
            const scaleX = finalWidth / img.width;
            const scaleY = finalHeight / img.height;
            
            // Calculate centered position
            const left = (canvasWidth - finalWidth) / 2;
            const top = (canvasHeight - finalHeight) / 2;
            
            // Set image properties
            img.set({
              left: left,
              top: top,
              scaleX: scaleX,
              scaleY: scaleY,
              selectable: true,
              metadata: {
                isEvidence: isEvidenceImage,
                evidenceData: isEvidenceImage ? imageData.evidence : null,
                source: isEvidenceImage ? 'evidence' : 'external'
              }
            });
            
            canvas.add(img);
            canvas.setActiveObject(img);
            canvas.renderAll();
            saveCanvasState();
            isImageLoading = false;
            resolve(img);
          } else {
            isImageLoading = false;
            reject(new Error('Failed to load image'));
          }
        },
        {
          crossOrigin: 'anonymous'
        }
      );
    });
  }

  /**
   * Delete the currently selected object from the canvas
   */
  export function deleteSelectedObject() {
    if (selectedObject) {
      saveCanvasState(); // Save the updated canvas state
      canvas.remove(selectedObject); // Remove the selected object from the canvas
      selectedObject = null; // Clear the reference
      canvas.renderAll();  //update canvas
      dispatch('updateSubMenu', { selectedObject: null }); // Update the submenu
    }
  }

  /**
   * Get the current canvas instance
   * @returns {object} - The Fabric.js canvas instance
   */
  export function getCanvas() {
    return canvas;
  }

  /**
   * Add an external image to the canvas by URL
   * @param {string} imageUrl - The URL of the image to add
   */
  export async function addExternalImageToCanvas(imageUrl) {
    console.log(`Adding external image to canvas: ${imageUrl}`);
    return new Promise((resolve, reject) => {
      fabric.Image.fromURL(
        imageUrl,
        function(img) {
          if (img) {
            // Get canvas dimensions
            const canvasWidth = canvas.width;
            const canvasHeight = canvas.height;
            const maxHeight = canvasHeight * 0.7; // 70% of canvas height
            
            // Calculate aspect ratios
            const imageRatio = img.width / img.height;
            const maxWidthFromHeight = maxHeight * imageRatio;
            
            let finalWidth, finalHeight;
            
            // If width when constrained by height is less than canvas width
            if (maxWidthFromHeight <= canvasWidth) {
              // Use max height as constraint
              finalHeight = maxHeight;
              finalWidth = maxWidthFromHeight;
            } else {
              // Use canvas width as constraint
              finalWidth = canvasWidth;
              finalHeight = canvasWidth / imageRatio;
            }
            
            // Calculate scale factors
            const scaleX = finalWidth / img.width;
            const scaleY = finalHeight / img.height;
            
            // Calculate centered position
            const left = (canvasWidth - finalWidth) / 2;
            const top = (canvasHeight - finalHeight) / 2;
            
            // Set image properties
            img.set({
              left: left,
              top: top,
              scaleX: scaleX,
              scaleY: scaleY,
              selectable: true
            });
            
            canvas.add(img);
            canvas.setActiveObject(img);
            canvas.renderAll();
            saveCanvasState();
            resolve(img);
          } else {
            reject(new Error('Failed to load image'));
          }
        },
        {
          crossOrigin: 'anonymous'
        }
      );
    });
  }

  export function bringForward() {
    if (selectedObject) {
      canvas.bringForward(selectedObject);
      saveCanvasState();
    }
  }

  export function sendBackward() {
    if (selectedObject) {
      canvas.sendBackward(selectedObject);
      saveCanvasState();
    }
  }

  export function bringToFront() {
    if (selectedObject) {
      canvas.bringToFront(selectedObject);
      saveCanvasState();
    }
  }

  export function sendToBack() {
    if (selectedObject) {
      canvas.sendToBack(selectedObject);
      saveCanvasState();
    }
  }

  export function setBorderWidth(width) {
    if (selectedObject && selectedObject.type === 'image') {
      selectedObject.set({
        strokeWidth: parseInt(width),
        stroke: width > 0 ? '#000000' : null // Add black border if width > 0
      });
      canvas.renderAll();
      saveCanvasState();
    }
  }

  export function cropImage() {
    if (!selectedObject || selectedObject.type !== 'image') return;
    
    const img = selectedObject;
    const imgBounds = img.getBoundingRect();
    const originalImage = img.getElement();
    const originalScale = {
      x: img.scaleX,
      y: img.scaleY,
      width: img.width,
      height: img.height,
      left: img.left,
      top: img.top
    };
    let isCropping = true;
    
    // Disable all canvas events and selection
    canvas.discardActiveObject();
    canvas.forEachObject(obj => {
      obj.selectable = false;
      obj.evented = false;
    });
    
    // Create overlay that covers entire canvas
    const overlay = new fabric.Rect({
      left: 0,
      top: 0,
      width: canvas.width,
      height: canvas.height,
      fill: 'rgba(0, 0, 0, 0.5)',
      selectable: false,
      evented: false,
      name: 'cropOverlay'
    });

    // Initialize crop rectangle at current image bounds
    const rect = new fabric.Rect({
      left: imgBounds.left,
      top: imgBounds.top,
      width: imgBounds.width,
      height: imgBounds.height,
      fill: 'transparent',
      stroke: '#ffffff',
      strokeWidth: 2,
      name: 'cropRect',
      cornerColor: '#ffffff',
      cornerStrokeColor: '#ffffff',
      transparentCorners: false,
      cornerSize: 14,
      cornerStyle: 'circle',
      lockRotation: true,
      hasRotatingPoint: false,
      selectable: true,
      evented: true,
      borderDashArray: [0, 0],
      borderScaleFactor: 2
    });

    // Add grid lines for rule of thirds
    const gridLines = [];
    const addGridLine = (coords) => {
      const line = new fabric.Line(coords, {
        stroke: 'rgba(255, 255, 255, 0.5)',
        strokeWidth: 1,
        selectable: false,
        evented: false,
        name: 'gridLine'
      });
      gridLines.push(line);
      canvas.add(line);
    };

    // Function to update grid lines
    function updateGridLines() {
      const bounds = rect.getBoundingRect();
      gridLines.forEach(line => canvas.remove(line));
      gridLines.length = 0;

      // Vertical lines
      for (let i = 1; i < 3; i++) {
        addGridLine([
          bounds.left + (bounds.width * i / 3), bounds.top,
          bounds.left + (bounds.width * i / 3), bounds.top + bounds.height
        ]);
      }
      // Horizontal lines
      for (let i = 1; i < 3; i++) {
        addGridLine([
          bounds.left, bounds.top + (bounds.height * i / 3),
          bounds.left + bounds.width, bounds.top + (bounds.height * i / 3)
        ]);
      }
    }

    // Create controls container with improved styling
    const controlsContainer = document.createElement('div');
    controlsContainer.className = 'crop-controls';
    controlsContainer.style.cssText = `
      position: absolute;
      top: 20px;
      right: 20px;
      display: flex;
      align-items: center;
      z-index: 9999;
    `;
    
    controlsContainer.innerHTML = `
      <button class="crop-btn cancel-crop" title="Cancel">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6.75 6.75L17.25 17.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          <path d="M17.25 6.75L6.75 17.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </button>
      <button class="crop-btn apply-crop" title="Apply">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4.5 12.75L9.5 17.75L19.5 6.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    `;

    // Add elements
    canvas.add(overlay);
    canvas.add(rect);
    canvas.setActiveObject(rect);
    updateGridLines();
    document.querySelector('.canvas-container2').appendChild(controlsContainer);

    // Update grid lines when crop rectangle changes
    rect.on('moving', updateGridLines);
    rect.on('scaling', updateGridLines);

    // Modify constraint handling to allow expanding
    function constrainCropRect(e) {
      if (e.target !== rect) return;
      
      const rectBounds = rect.getBoundingRect();
      const imgBounds = img.getBoundingRect();
      let modified = false;
      
      // Minimum size (20% of original)
      const minWidth = originalScale.width * 0.2;
      const minHeight = originalScale.height * 0.2;
      
      // Maximum size (original image dimensions)
      const maxWidth = originalScale.width;
      const maxHeight = originalScale.height;

      if (rectBounds.width < minWidth) {
        rect.scaleX = rect.scaleX * (minWidth / rectBounds.width);
        modified = true;
      }
      if (rectBounds.height < minHeight) {
        rect.scaleY = rect.scaleY * (minHeight / rectBounds.height);
        modified = true;
      }

      // Allow expanding up to original image bounds
      const originalBounds = {
        left: originalScale.left,
        top: originalScale.top,
        width: originalScale.width * originalScale.x,
        height: originalScale.height * originalScale.y
      };

      const newLeft = Math.max(originalBounds.left, Math.min(rectBounds.left, originalBounds.left + originalBounds.width - rectBounds.width));
      const newTop = Math.max(originalBounds.top, Math.min(rectBounds.top, originalBounds.top + originalBounds.height - rectBounds.height));
      
      if (newLeft !== rectBounds.left) {
        rect.left = newLeft;
        modified = true;
      }
      if (newTop !== rectBounds.top) {
        rect.top = newTop;
        modified = true;
      }

      if (modified) {
        rect.setCoords();
        updateGridLines();
      }
    }

    // Handle clicking outside crop area
    canvas.on('mouse:down', function(e) {
      if (isCropping && (!e.target || (e.target !== rect && e.target !== overlay))) {
        cancelCrop();
      }
    });

    // Button handlers
    const applyButton = controlsContainer.querySelector('.apply-crop');
    const cancelButton = controlsContainer.querySelector('.cancel-crop');

    applyButton.addEventListener('click', () => {
      applyCrop();
      cleanup();
    });

    cancelButton.addEventListener('click', cancelCrop);

    // Modify the applyCrop function to handle expanded crops
    function applyCrop() {
      const scaleX = originalScale.width / (originalScale.width * originalScale.x);
      const scaleY = originalScale.height / (originalScale.height * originalScale.y);

      // Calculate crop dimensions relative to original image
      const cropX = Math.round((rect.left - originalScale.left) * scaleX);
      const cropY = Math.round((rect.top - originalScale.top) * scaleY);
      const cropWidth = Math.round(rect.width * rect.scaleX * scaleX);
      const cropHeight = Math.round(rect.height * rect.scaleY * scaleY);

      // Create temporary canvas
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      tempCanvas.width = cropWidth;
      tempCanvas.height = cropHeight;

      // Draw the cropped portion from original image
      tempCtx.drawImage(
        originalImage,
        cropX, cropY, cropWidth, cropHeight,
        0, 0, cropWidth, cropHeight
      );

      // Create new image from cropped canvas
      const cropped = new Image();
      cropped.src = tempCanvas.toDataURL('image/png');

      cropped.onload = function() {
        const croppedImg = new fabric.Image(cropped, {
          left: rect.left,
          top: rect.top,
          scaleX: img.scaleX,
          scaleY: img.scaleY,
          selectable: true,
          evented: true,
          metadata: img.metadata // Preserve metadata
        });
        
        // Store original image data in metadata for future crops
        croppedImg.metadata = {
          ...croppedImg.metadata,
          originalImage: {
            element: originalImage,
            width: originalScale.width,
            height: originalScale.height,
            scaleX: originalScale.x,
            scaleY: originalScale.y,
            left: originalScale.left,
            top: originalScale.top
          }
        };

        canvas.remove(img);
        canvas.add(croppedImg);
        canvas.setActiveObject(croppedImg);
        saveCanvasState();
      };
    }

    function cancelCrop() {
      if (!isCropping) return;
      isCropping = false;
      cleanup();
    }

    function cleanup() {
      canvas.off('mouse:down');
      canvas.off('object:moving', constrainCropRect);
      canvas.off('object:scaling', constrainCropRect);
      canvas.remove(overlay);
      canvas.remove(rect);
      controlsContainer.remove();
      
      canvas.forEachObject(obj => {
        obj.selectable = true;
        obj.evented = true;
      });
      
      canvas.setActiveObject(img);
      canvas.renderAll();
    }

    // Add double-click handler to crop rectangle
    rect.on('mousedblclick', function() {
      applyCrop();
      cleanup();
    });
  }

  // Helper function for moving objects with arrow keys
  function moveSelectedObject(deltaX, deltaY) {
    if (selectedObject) {
      selectedObject.set({
        left: selectedObject.left + deltaX,
        top: selectedObject.top + deltaY
      });
      canvas.renderAll();
      saveCanvasState();
    }
  }

  // Helper function for selecting all objects
  function selectAllObjects() {
    const objects = canvas.getObjects();
    if (objects.length > 0) {
      const selection = new fabric.ActiveSelection(objects, { canvas: canvas });
      canvas.setActiveSelection(selection);
      canvas.requestRenderAll();
    }
  }

  function zoomIn() {
    const zoom = canvas.getZoom();
    canvas.setZoom(zoom * 1.1);
    canvas.renderAll();
  }

  function zoomOut() {
    const zoom = canvas.getZoom();
    canvas.setZoom(zoom / 1.1);
    canvas.renderAll();
  }

  function resetZoom() {
    canvas.setZoom(1);
    canvas.renderAll();
  }

  export function generatePreviewForSlide(canvasState) {
    return new Promise((resolve, reject) => {
      try {
        // Create a temporary canvas
        const tempCanvas = new fabric.Canvas(null, {
          width: 375,
          height: 667
        });
        
        // Load the canvas state
        if (canvasState && canvasState.length > 0) {
          tempCanvas.loadFromJSON({ objects: canvasState }, () => {
            const scaleFactor = 0.5;
            
            // Create another temp canvas for the scaled version
            const previewCanvas = new fabric.Canvas(null, {
              width: 375 * scaleFactor,
              height: 667 * scaleFactor
            });
            
            // Clone and scale all objects
            tempCanvas.getObjects().forEach(obj => {
              const clonedObj = fabric.util.object.clone(obj);
              clonedObj.scaleX = obj.scaleX * scaleFactor;
              clonedObj.scaleY = obj.scaleY * scaleFactor;
              clonedObj.left = obj.left * scaleFactor;
              clonedObj.top = obj.top * scaleFactor;
              previewCanvas.add(clonedObj);
            });
            
            // Generate optimized SVG
            const svgData = previewCanvas.toSVG({
              viewBox: {
                x: 0,
                y: 0,
                width: 375 * scaleFactor,
                height: 667 * scaleFactor
              },
              width: 375 * scaleFactor,
              height: 667 * scaleFactor,
              suppressPreamble: false,
              preserveAspectRatio: 'xMidYMid meet'
            });
            
            // Clean up
            tempCanvas.dispose();
            previewCanvas.dispose();
            
            // Optimize SVG
            const optimizedSvg = svgData
              .replace(/\s+/g, ' ')
              .replace(/>\s+</g, '><')
              .replace(/\s+>/g, '>')
              .replace(/\s+\/>/g, '/>')
              .replace(/<svg/, `<svg width="${375 * scaleFactor}" height="${667 * scaleFactor}"`);
            
            resolve(optimizedSvg);
          });
        } else {
          // If no canvas state, return empty string
          resolve('');
        }
      } catch (err) {
        console.error('Error generating preview:', err);
        reject(err);
      }
    });
  }

  // Handle pasting from clipboard
  function handleClipboardPaste(event) {
    console.log('💾 Paste event detected', event);
    
    // Skip if user is typing in an input/textarea
    if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
      console.log('Paste target is input or textarea, ignoring');
      return;
    }
    
    try {
      // Check for image items in clipboard
      const clipboardData = event.clipboardData || window.clipboardData;
      console.log('Clipboard data available:', !!clipboardData);
      
      if (!clipboardData) {
        console.warn('No clipboard data available');
        return;
      }
      
      console.log('Clipboard types:', clipboardData.types);
      
      // Method 1: Check if files are available directly (works in some browsers)
      if (clipboardData.files && clipboardData.files.length > 0) {
        const file = clipboardData.files[0];
        console.log('Found file in clipboard:', file.type, file.name);
        
        if (file.type.startsWith('image/')) {
          event.preventDefault();
          console.log('Processing image file from clipboard');
          
          const reader = new FileReader();
          reader.onload = function(e) {
            console.log('File reader completed loading image');
            addImageFromDataUrl(e.target.result);
          };
          
          reader.onerror = function(error) {
            console.error('Error reading file:', error);
            isImageLoading = false;
          };
          
          reader.readAsDataURL(file);
          return;
        }
      }
      
      // Method 2: Safari and some browsers support direct HTML paste
      if (clipboardData.types.includes('text/html')) {
        const html = clipboardData.getData('text/html');
        console.log('Found HTML in clipboard');
        
        // Check if HTML contains an image
        const imgMatch = html.match(/<img[^>]+src="([^"]+)"[^>]*>/i);
        if (imgMatch && imgMatch[1]) {
          event.preventDefault();
          
          const imgSrc = imgMatch[1];
          console.log('Found image in HTML:', imgSrc);
          
          // Check if it's a data URL or a remote URL
          if (imgSrc.startsWith('data:image/')) {
            console.log('Processing data URL from HTML paste');
            addImageFromDataUrl(imgSrc);
            return;
          } else if (imgSrc.startsWith('http') || imgSrc.startsWith('https')) {
            console.log('Processing remote image URL from HTML paste');
            addExternalImageToCanvas(imgSrc)
              .then(() => console.log('Remote image added successfully'))
              .catch(err => console.error('Failed to add remote image:', err));
            return;
          }
        }
      }
      
      // Method 3: Check items (works in modern browsers)
      const items = clipboardData.items;
      console.log('Clipboard items available:', !!items);
      
      if (!items) {
        console.warn('No items in clipboard data');
        return;
      }
      
      // Log all available items
      for (let i = 0; i < items.length; i++) {
        console.log(`Clipboard item ${i}:`, items[i].kind, items[i].type);
      }
      
      // Find image item in clipboard data
      let hasFoundImage = false;
      
      for (let i = 0; i < items.length; i++) {
        if (items[i].kind === 'file' && items[i].type.startsWith('image/')) {
          hasFoundImage = true;
          event.preventDefault(); // Prevent default paste behavior
          
          console.log(`Processing image from clipboard item ${i}:`, items[i].type);
          
          // Get the blob image from clipboard
          const blob = items[i].getAsFile();
          console.log('Image blob created:', !!blob);
          
          if (!blob) {
            console.warn('Failed to get file from clipboard item');
            continue;
          }
          
          // Convert blob to data URL
          const reader = new FileReader();
          
          reader.onload = function(e) {
            console.log('FileReader load completed successfully');
            const dataUrl = e.target.result;
            console.log('Data URL created with length:', dataUrl.length);
            
            // Add image to canvas
            addImageFromDataUrl(dataUrl);
          };
          
          reader.onerror = function(error) {
            console.error('FileReader error:', error);
            isImageLoading = false;
          };
          
          console.log('Starting to read blob as data URL');
          reader.readAsDataURL(blob);
          return; // Exit after handling image
        }
      }
      
      // Method 4: Fallback for Safari - use Canvas API to capture image
      try {
        // This fallback uses canvas to render any visual content from clipboard
        if (!hasFoundImage && !clipboardData.types.includes('text/plain')) {
          console.log('Trying canvas fallback for image paste');
          
          // Create a temporary img element
          const tempImg = new Image();
          const tempCanvas = document.createElement('canvas');
          const tempCtx = tempCanvas.getContext('2d');
          
          // When the image loads, draw it to the canvas and get the data URL
          tempImg.onload = function() {
            console.log('Temp image loaded:', tempImg.width, 'x', tempImg.height);
            
            // Skip if the image is empty or invalid
            if (tempImg.width <= 1 || tempImg.height <= 1) {
              console.log('Invalid temp image size, skipping');
              return;
            }
            
            tempCanvas.width = tempImg.width;
            tempCanvas.height = tempImg.height;
            tempCtx.drawImage(tempImg, 0, 0);
            
            try {
              const dataUrl = tempCanvas.toDataURL('image/png');
              console.log('Canvas data URL created');
              addImageFromDataUrl(dataUrl);
            } catch (err) {
              console.error('Error converting canvas to data URL:', err);
            }
          };
          
          // Set the source to a new blob URL from the clipboard
          for (let i = 0; i < items.length; i++) {
            if (items[i].type.startsWith('image/')) {
              const blob = items[i].getAsFile();
              if (blob) {
                tempImg.src = URL.createObjectURL(blob);
                event.preventDefault();
                return;
              }
            }
          }
        }
      } catch (canvasErr) {
        console.error('Canvas fallback error:', canvasErr);
      }
      
      if (!hasFoundImage) {
        console.log('No image items found in clipboard. Items found:', items.length);
      }
    } catch (error) {
      console.error('Error in clipboard paste handler:', error);
      isImageLoading = false;
    }
  }
  
  // Helper function to add image from data URL
  function addImageFromDataUrl(dataUrl) {
    console.log('📸 Adding image from data URL...');
    isImageLoading = true;
    
    // Show loading indicator
    canvas.requestRenderAll();
    
    // Create a timeout to prevent hanging on large images
    const imageTimeout = setTimeout(() => {
      console.warn('Image processing timed out after 10 seconds');
      isImageLoading = false;
    }, 10000);
    
    // First, create an actual Image element to load the data URL
    const actualImage = new Image();
    actualImage.onload = function() {
      // Ensure we clear the timeout as soon as the image loads
      clearTimeout(imageTimeout);
      
      console.log('Image element loaded from data URL, dimensions:', actualImage.width, 'x', actualImage.height);
      
      // Create a temporary canvas to possibly resize and optimize the image
      const tempCanvas = document.createElement('canvas');
      
      // Limit maximum dimensions while preserving aspect ratio
      // This prevents huge images from consuming too much memory
      let imgWidth = actualImage.width;
      let imgHeight = actualImage.height;
      const MAX_DIMENSION = 1200; // Reduced max dimension for better performance
      
      if (imgWidth > MAX_DIMENSION || imgHeight > MAX_DIMENSION) {
        if (imgWidth > imgHeight) {
          imgHeight = Math.round(imgHeight * (MAX_DIMENSION / imgWidth));
          imgWidth = MAX_DIMENSION;
        } else {
          imgWidth = Math.round(imgWidth * (MAX_DIMENSION / imgHeight));
          imgHeight = MAX_DIMENSION;
        }
        console.log('Resized large image to:', imgWidth, 'x', imgHeight);
      }
      
      tempCanvas.width = imgWidth;
      tempCanvas.height = imgHeight;
      const ctx = tempCanvas.getContext('2d');
      ctx.drawImage(actualImage, 0, 0, imgWidth, imgHeight);
      
      try {
        // First try with JPEG format at moderate quality for better compression
        // Most clipboard images don't need PNG transparency
        let optimizedDataUrl;
        let format = 'image/jpeg';
        let quality = 0.8;
        
        // Check if the image has transparency (need PNG format)
        const hasTransparency = checkImageForTransparency(ctx, imgWidth, imgHeight);
        
        if (hasTransparency) {
          // Use PNG with lower quality if transparency is needed
          console.log('Image has transparency, using PNG format');
          format = 'image/png';
          quality = 0.6;
        } else {
          console.log('No transparency detected, using JPEG format for better compression');
        }
        
        optimizedDataUrl = tempCanvas.toDataURL(format, quality);
        console.log(`Created optimized ${format} data URL, length:`, optimizedDataUrl.length);
        
        // If data URL is still large, try to reduce quality further
        if (optimizedDataUrl.length > 500000) {
          console.log('Data URL is still large, reducing quality further');
          quality = format === 'image/png' ? 0.4 : 0.6;
          optimizedDataUrl = tempCanvas.toDataURL(format, quality);
          console.log(`Reduced quality data URL, new length:`, optimizedDataUrl.length);
        }
        
        // Further compression for very large images
        if (optimizedDataUrl.length > 1000000) {
          console.log('Data URL is extremely large, reducing dimensions');
          
          // Create a smaller canvas
          const smallerCanvas = document.createElement('canvas');
          const scaleFactor = 0.75;
          const smallerWidth = Math.round(imgWidth * scaleFactor);
          const smallerHeight = Math.round(imgHeight * scaleFactor);
          
          smallerCanvas.width = smallerWidth;
          smallerCanvas.height = smallerHeight;
          
          const smallerCtx = smallerCanvas.getContext('2d');
          smallerCtx.drawImage(tempCanvas, 0, 0, imgWidth, imgHeight, 0, 0, smallerWidth, smallerHeight);
          
          // Try with reduced dimensions
          quality = format === 'image/png' ? 0.3 : 0.5;
          optimizedDataUrl = smallerCanvas.toDataURL(format, quality);
          console.log(`Created smaller ${format} data URL, length:`, optimizedDataUrl.length);
        }
        
        // Verify the data URL is valid and not corrupted
        if (!optimizedDataUrl || optimizedDataUrl.length < 100 || optimizedDataUrl.includes('compressed')) {
          throw new Error('Generated data URL appears invalid or corrupted');
        }
        
        // Now create the fabric image from the optimized data URL
        fabric.Image.fromURL(optimizedDataUrl, function(img) {
          if (!img) {
            console.error('Failed to create fabric image from optimized data URL');
            isImageLoading = false;
            return;
          }
          
          console.log('Image successfully created from optimized data URL');
          console.log('Image dimensions:', img.width, 'x', img.height);
          
          // Get canvas dimensions
          const canvasWidth = canvas.width;
          const canvasHeight = canvas.height;
          const maxHeight = canvasHeight * 0.7; // 70% of canvas height
          
          // Calculate aspect ratios
          const imageRatio = img.width / img.height;
          const maxWidthFromHeight = maxHeight * imageRatio;
          
          let finalWidth, finalHeight;
          
          // If width when constrained by height is less than canvas width
          if (maxWidthFromHeight <= canvasWidth) {
            // Use max height as constraint
            finalHeight = maxHeight;
            finalWidth = maxWidthFromHeight;
          } else {
            // Use canvas width as constraint
            finalWidth = canvasWidth;
            finalHeight = canvasWidth / imageRatio;
          }
          
          console.log('Calculated dimensions:', finalWidth, 'x', finalHeight);
          
          // Calculate scale factors
          const scaleX = finalWidth / img.width;
          const scaleY = finalHeight / img.height;
          
          // Calculate centered position
          const left = (canvasWidth - finalWidth) / 2;
          const top = (canvasHeight - finalHeight) / 2;
          
          // Store a custom property with the direct data URL
          // This ensures we always have a reference to the original data
          const embeddedImageData = optimizedDataUrl;
          
          // Set image properties and store the data URL directly
          img.set({
            left: left,
            top: top,
            scaleX: scaleX,
            scaleY: scaleY,
            selectable: true,
            crossOrigin: 'anonymous',
            clipboardImage: true, // Custom property for easier identification
            src: embeddedImageData, // Store the data URL as the source
            metadata: {
              source: 'clipboard',
              isClipboardImage: true,
              // Store the data URL in metadata to ensure it's preserved
              embeddedDataUrl: embeddedImageData,
              format: format, // Store the format used
              hasTransparency: hasTransparency // Store transparency info
            }
          });
          
          // Add a custom method to ensure we can always access the data URL
          img.getEmbeddedImageData = function() {
            return embeddedImageData;
          };
          
          // Now add the image to canvas
          canvas.add(img);
          canvas.setActiveObject(img);
          canvas.renderAll();
          
          // Save canvas state with image data embedded
          saveCanvasState();
          
          // Clear loading state
          isImageLoading = false;
          console.log('✅ Image added to canvas successfully!');
        }, { 
          crossOrigin: 'anonymous'
        });
      } catch (error) {
        console.error('Error processing image data:', error);
        clearTimeout(imageTimeout);
        
        // Fallback to JPEG with lower quality if PNG failed
        try {
          console.log('Attempting JPEG fallback with lower quality');
          const jpegDataUrl = tempCanvas.toDataURL('image/jpeg', 0.7);
          
          fabric.Image.fromURL(jpegDataUrl, function(img) {
            if (!img) {
              console.error('Failed to create fabric image from JPEG fallback');
              isImageLoading = false;
              return;
            }
            
            console.log('Image created using JPEG fallback');
            
            // Get canvas dimensions and position image
            const canvasWidth = canvas.width;
            const canvasHeight = canvas.height;
            const maxHeight = canvasHeight * 0.7;
            const imageRatio = img.width / img.height;
            const maxWidthFromHeight = maxHeight * imageRatio;
            
            let finalWidth, finalHeight;
            if (maxWidthFromHeight <= canvasWidth) {
              finalHeight = maxHeight;
              finalWidth = maxWidthFromHeight;
            } else {
              finalWidth = canvasWidth;
              finalHeight = canvasWidth / imageRatio;
            }
            
            const scaleX = finalWidth / img.width;
            const scaleY = finalHeight / img.height;
            const left = (canvasWidth - finalWidth) / 2;
            const top = (canvasHeight - finalHeight) / 2;
            
            // Set image properties with JPEG data URL
            img.set({
              left: left,
              top: top,
              scaleX: scaleX,
              scaleY: scaleY,
              selectable: true,
              crossOrigin: 'anonymous',
              clipboardImage: true,
              src: jpegDataUrl,
              metadata: {
                source: 'clipboard',
                isClipboardImage: true,
                embeddedDataUrl: jpegDataUrl,
                format: 'image/jpeg',
                hasTransparency: false
              }
            });
            
            // Add image to canvas
            canvas.add(img);
            canvas.setActiveObject(img);
            canvas.renderAll();
            saveCanvasState();
            isImageLoading = false;
            console.log('✅ Image added to canvas using JPEG fallback');
          }, { crossOrigin: 'anonymous' });
        } catch (fallbackError) {
          console.error('Fallback also failed:', fallbackError);
          isImageLoading = false;
        }
      }
    };
    
    actualImage.onerror = function(error) {
      console.error('Error loading image from data URL:', error);
      clearTimeout(imageTimeout);
      isImageLoading = false;
    };
    
    // Set the source to the data URL
    actualImage.src = dataUrl;
  }

  // Utility function to check if an image has transparency
  function checkImageForTransparency(ctx, width, height) {
    try {
      // Get image data
      const imageData = ctx.getImageData(0, 0, width, height);
      const data = imageData.data;
      
      // Sample pixels for transparency (check every 10th pixel)
      // Checking every pixel would be slow for large images
      for (let i = 3; i < data.length; i += 40) {
        if (data[i] < 255) {
          return true; // Found transparent pixel
        }
      }
      
      return false; // No transparent pixels found
    } catch (e) {
      console.warn('Error checking transparency:', e);
      return false; // Assume no transparency on error
    }
  }

  // Trigger paste event programmatically 
  export function triggerPaste() {
    console.log('Manually triggering paste event');
    // Request clipboard access and handle the paste
    try {
      if (navigator.clipboard && typeof navigator.clipboard.read === 'function') {
        navigator.clipboard.read()
          .then(clipboardItems => {
            console.log('Clipboard items:', clipboardItems);
            
            // Process all items 
            for (const clipboardItem of clipboardItems) {
              for (const type of clipboardItem.types) {
                if (type.startsWith('image/')) {
                  console.log('Found image type in clipboard:', type);
                  
                  // Get the image blob
                  clipboardItem.getType(type)
                    .then(blob => {
                      console.log('Got blob from clipboard:', blob);
                      
                      // Convert blob to data URL
                      const reader = new FileReader();
                      reader.onload = e => {
                        console.log('Converted blob to data URL');
                        addImageFromDataUrl(e.target.result);
                      };
                      reader.readAsDataURL(blob);
                    })
                    .catch(err => {
                      console.error('Error getting clipboard content:', err);
                      // Fallback - create a synthetic paste event
                      document.execCommand('paste');
                    });
                  
                  return; // Exit after finding first image
                }
              }
            }
            
            // If we get here, we couldn't find any image types
            console.log('No image found in clipboard API, trying fallback');
            document.execCommand('paste');
          })
          .catch(err => {
            console.error('Error accessing clipboard:', err);
            // Fallback for browsers without clipboard API
            document.execCommand('paste');
          });
      } else {
        console.log('Clipboard API not available, using execCommand fallback');
        document.execCommand('paste');
      }
    } catch (error) {
      console.error('Error with clipboard API:', error);
      // Fallback for older browsers
      document.execCommand('paste');
    }
  }
</script>

<div class="canvas-container2" tabindex="0">
  {#if isImageLoading}
    <div class="loading-overlay">
      <div class="spinner"></div>
    </div>
  {/if}
  <canvas id="story-canvas"></canvas>
</div>

<style>
  .canvas-container {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    height: 100%;
  }

  #story-canvas {
    border: 1px solid #ccc;
    width: 375px;
    height: 667px;
    border-radius: 20px;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
    background-color: white;
  }

  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }

  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .canvas-container2 {
    position: relative;
    /* Add styles to show focus */
    outline: none;
  }
  
  .canvas-container2:focus {
    outline: 2px solid rgba(59, 130, 246, 0.5);
    outline-offset: 2px;
    border-radius: 20px;
  }

  .canvas-container2 .crop-controls {
    position: absolute;
    z-index: 9999;
    background: rgba(0, 0, 0, 0.85);
    backdrop-filter: blur(12px);
    padding: 8px;
    border-radius: 12px;
    display: flex;
    gap: 6px;
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  .canvas-container2 .crop-controls .crop-btn {
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    background: transparent;
    color: white;
    padding: 0;
  }

  .canvas-container2 .crop-controls .crop-btn svg {
    width: 18px;
    height: 18px;
    opacity: 0.9;
    transition: all 0.2s ease;
  }

  .canvas-container2 .crop-controls .cancel-crop:hover {
    background: rgba(239, 68, 68, 0.15);
    color: rgb(239, 68, 68);
  }

  .canvas-container2 .crop-controls .apply-crop:hover {
    background: rgba(34, 197, 94, 0.15);
    color: rgb(34, 197, 94);
  }

  .canvas-container2 .crop-controls .crop-btn:hover svg {
    opacity: 1;
    transform: scale(1.1);
  }

  /* Update tooltip styles */
  .canvas-container2 .crop-controls .crop-btn:hover::after {
    content: attr(title);
    position: absolute;
    bottom: -24px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.85);
    color: white;
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 500;
    white-space: nowrap;
    letter-spacing: 0.3px;
  }

  /* Remove previous animation styles */
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .canvas-container2 .crop-controls {
    animation: fadeIn 0.2s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }
</style>